// D3-6 / D3-7 — THE USAGE PAGE. Per member, owner-only, behind the support passcode, READ-ONLY.
//
// D3-6 CALLED "View usage" A DEAD BUTTON. IT WAS NOT DEAD. D5-2 measured it: the handler always
// worked and the panel rendered 13,233px below the fold on her phone. So there was nothing to
// rewire — the door needed to LAND IN FRONT OF HER, which is what the modal did. This page is what
// that door now opens onto, and it renders inside the same modal for the same reason.
//
// It mounts only when the support door is already open, because it is passed in as the support
// panel's `extra` — so there is no state here that could disagree with the door's state.
//
// R79 — **THE THIRD HAND-TYPED COPY OF "WHICH 403 IS THIS", AND THE THREE HAD ALREADY DRIFTED.**
// `OwnerSupportView` wrote the law down in a comment, this page and `OwnerTranscript` each retyped
// it, and the else-branches disagree: the transcript falls back to "That could not be opened." and
// this one fell back to `""` — **so a locked-out or malformed 403 here said nothing at all, under a
// "View usage" button she had just pressed** (R47: the count is the finding). One law, in
// `src/lib/support-read.ts`. And `if (d?.ok) setData(d)` had no presence guard, so a partial payload
// renders `data.lala.turnsPerDay` and takes the dashboard down with it — a blank page rather than a
// wrong sentence, which is the one failure mode on these surfaces that is worse than silence.
import { useCallback, useEffect, useState } from "react";
import { costClassNote, TRANSCRIPT_NOTE } from "../usagePage.js";
import { loadSupportData, supportInnerNote } from "../lib/support-read";
import { OwnerTranscript } from "./OwnerTranscript";
import { useRevealHere } from "./useRevealHere";

type HistoryItem = { id: string; type: string; title: string; at: string; status: string; inMyRoom: boolean; costClass: string; room: string };
type Conversation = { id: string; title: string; collection: string; messageCount: number; at: string };
type Usage = {
  member: { id: string; email: string; name: string };
  history: { items: HistoryItem[]; generationsRecorded: number; since: string | null };
  lala: {
    conversations: Conversation[]; drawerMessages: number; drawerLastAt: string;
    turnsByDay: Array<{ day: string; turns: number }>;
    gateTrips: Array<{ gate: string; outcome: string; count: number; lastAt: string }>;
    tier: string; turnsPerDay: number | null;
  };
  costClasses: Record<string, string>;
  transcriptNote: string;
  walls: string[];
};

const day = (iso: string) => (iso ? new Date(iso).toLocaleDateString() : "");

export function OwnerUsagePage({ memberId, onDenied, onOpenReport }: {
  memberId: string;
  onDenied: () => void;
  onOpenReport?: (reportId: string) => void;
}) {
  const [data, setData] = useState<Usage | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState<string | null>(null);
  // D5-2, the fourth time: a view that replaces another inside a scrolling modal inherits the old
  // scroll position. Measured at top=-194 before this line existed.
  const here = useRevealHere<HTMLDivElement>([data, reading]);

  const load = useCallback(async () => {
    setBusy(true); setNote("");
    const answer = await loadSupportData<Usage>(`/api/owner/support/member/${encodeURIComponent(memberId)}/usage`);
    setBusy(false);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    // R53's presence guard: a 200 that is missing the two blocks this page is built from is a
    // refusal, not a page. Held by position, so the paint can never precede the check.
    if (answer.ok && answer.data?.lala && answer.data?.history) { setData(answer.data); return; }
    // A locked 403 SAYS SO here for the same reason it does in the transcript: this page mounts
    // inside the support panel's already-open door and has nothing of its own to re-read.
    // R88: that is `supportInnerNote` now — R79 declared the rule in a comment on BOTH surfaces and
    // typed the expression on both, which is the count that made it worth a name (R47).
    setNote(answer.ok ? "That could not be opened." : supportInnerNote(answer, "That could not be opened."));
  }, [memberId, onDenied]);

  useEffect(() => { setData(null); setReading(null); }, [memberId]);

  if (reading !== null && data) {
    // The transcript REPLACES the page rather than appending under it — the third time this round
    // that mattered, and the same reason each time: an answer below a long list is an answer she has
    // to hunt for.
    return (
      <OwnerTranscript memberId={memberId} conversationId={reading} onClose={() => setReading(null)} onDenied={onDenied} />
    );
  }

  if (!data) {
    return (
      <div className="owner-usage">
        <button type="button" className="button" disabled={busy} onClick={() => { void load(); }}>View usage</button>
        {note ? <p className="owner-page__note owner-fresh--failed" role="status">{note}</p> : null}
      </div>
    );
  }

  const cap = data.lala.turnsPerDay;

  return (
    <div className="owner-usage" data-usage-member={data.member.id} ref={here}>
      <div className="owner-detail__head">
        <h3 className="owner-detail__sub">Usage</h3>
        <button type="button" className="owner-link" onClick={() => setData(null)}>Close usage</button>
      </div>

      {/* ---------- LALA USAGE: the cap first, because it is the number she is asked about. ---------- */}
      <h3 className="owner-detail__sub">Ask Lala</h3>
      <p className="owner-page__note">
        {data.lala.tier ? `On the ${data.lala.tier} tier. ` : ""}
        {cap === null ? "Uncapped." : `${cap} turns a day.`}
        {data.lala.drawerMessages ? ` Drawer: ${data.lala.drawerMessages} messages, last ${day(data.lala.drawerLastAt)}.` : " No drawer messages."}
      </p>

      {data.lala.turnsByDay.length ? (
        <ul className="owner-bars owner-usage__turns">
          {data.lala.turnsByDay.map((t) => (
            <li key={t.day} data-turn-day={t.day}>
              <span className="owner-bars__label">{t.day}</span>
              {/* Against their cap, said as a fraction, so "did they hit the wall" is answerable at a
                  glance instead of by arithmetic. */}
              <span className="owner-bars__count">{cap === null ? `${t.turns}` : `${t.turns} / ${cap}`}</span>
            </li>
          ))}
        </ul>
      ) : <p className="owner-page__note">No turns recorded in the last 30 days.</p>}

      {/* ---------- THE CONVERSATIONS, AND THE DOOR INTO EACH ---------- */}
      <h3 className="owner-detail__sub">Conversations</h3>
      <p className="owner-page__note">{data.transcriptNote || TRANSCRIPT_NOTE}</p>
      <ul className="owner-bars owner-usage__conversations">
        {data.lala.drawerMessages ? (
          <li data-conversation="drawer">
            <span className="owner-bars__label">The drawer ({data.lala.drawerMessages} messages)</span>
            <button type="button" className="owner-link" onClick={() => setReading("drawer")}>Read transcript</button>
          </li>
        ) : null}
        {data.lala.conversations.map((c) => (
          <li key={c.id} data-conversation={c.id}>
            <span className="owner-bars__label">{c.title} ({c.messageCount} messages, {day(c.at)})</span>
            <button type="button" className="owner-link" onClick={() => setReading(c.id)}>Read transcript</button>
          </li>
        ))}
      </ul>
      {!data.lala.conversations.length && !data.lala.drawerMessages ? <p className="owner-page__note">No conversations.</p> : null}

      {/* ---------- GATE TRIPS ON THIS ACCOUNT ---------- */}
      {data.lala.gateTrips.length ? (
        <>
          <h3 className="owner-detail__sub">Gate trips on this account</h3>
          <ul className="owner-bars">
            {data.lala.gateTrips.map((g) => (
              <li key={`${g.gate}-${g.outcome}`} data-gate-trip={g.gate}>
                <span className="owner-bars__label">{g.gate}{g.outcome ? ` · ${g.outcome}` : ""}</span>
                <span className="owner-bars__count">{g.count}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* ---------- GENERATION HISTORY ---------- */}
      <h3 className="owner-detail__sub">What they have generated</h3>
      <p className="owner-page__note">
        {data.history.items.length} saved readings.
        {data.history.generationsRecorded
          ? ` ${data.history.generationsRecorded} generations recorded since ${day(data.history.since || "")} — a generation that was never saved has no row to open.`
          : ""}
      </p>
      <div className="owner-table-wrap">
        <table className="owner-table">
          <thead>
            <tr><th>When</th><th>Reading</th><th>Room</th><th>Status</th><th>Cost class</th><th /></tr>
          </thead>
          <tbody>
            {data.history.items.map((r) => (
              <tr key={r.id} data-history-report={r.id} data-cost-class={r.costClass}>
                <td>{day(r.at)}</td>
                <td>{r.title || r.type}</td>
                <td>{r.room || "—"}</td>
                <td>{r.status}</td>
                <td title={data.costClasses?.[r.costClass] || costClassNote(r.costClass)}>{r.costClass}</td>
                <td>
                  {onOpenReport && r.status === "completed"
                    ? <button type="button" className="owner-link" onClick={() => onOpenReport(r.id)}>Read as member</button>
                    : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.history.items.length ? <p className="owner-page__note">Nothing generated yet.</p> : null}
    </div>
  );
}
